import { useCallback, useEffect, useMemo, useRef } from 'react';
// hooks
import useCheckout from 'src/hooks/use-checkout'
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import {
  resetCustomers,
  updateCustomers,
  updateCustomer,
  fetchOneCustomer,
  fetchCustomersData,
} from 'src/redux/slice/customer';
import { selectCustomer } from 'src/redux/slice/checkout'
// auth
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export default function useCustomers() {
  const dispatch = useDispatch();
  const { user } = useAuthContext()
  const { customerSelected } = useCheckout()

  const { allData: customers, isLoading, error, nextPage } = useSelector((state) => state.customers)

  const config = user?.configSelected?.id

  const autoLoadKeyRef = useRef(null);

  const loadAllCustomerPages = useCallback(
    async (startPage = 1) => {
      if (!user?.id || !config) return;

      let page = startPage || 1;
      const loadedPages = new Set();

      while (page && typeof page === 'number' && page > 0 && !loadedPages.has(page)) {
        loadedPages.add(page);

        const result = await dispatch(
          fetchCustomersData({ idUser: user.id, config, page })
        ).unwrap();

        console.log('loadAllCustomerPages result', result);

        page = result.nextPage;
      }
    },
    [dispatch, user?.id, config]
  );

  // Auto-initialize customers on mount if not already loaded
  useEffect(() => {
    const loadKey = user?.id && config ? `${user.id}:${config}` : null;

    if (!customers[0] && !isLoading && loadKey && autoLoadKeyRef.current !== loadKey) {
      autoLoadKeyRef.current = loadKey;
      loadAllCustomerPages(nextPage || 1).catch((err) => {
        autoLoadKeyRef.current = null;
        console.error('Error fetching customers:', err);
      });
    }
  }, [customers, isLoading, user?.id, config, nextPage, loadAllCustomerPages]);

  const onRefreshCustomers = useCallback(
    async () => {
      if (!user?.id || !config) return;

      autoLoadKeyRef.current = `${user.id}:${config}`;

      // Reset existing data before refreshing
      dispatch(resetCustomers());

      try {
        await loadAllCustomerPages(1);
      } catch (err) {
        autoLoadKeyRef.current = null;
        console.error('Error refreshing customers:', err);
      }
    },
    [dispatch, user?.id, config, loadAllCustomerPages]
  );

  const onRefreshOneCustomer = useCallback(
    (id) => dispatch(fetchOneCustomer({ idUser: user?.id, config, id }))
      .unwrap()
      .catch((error) => {
        console.error('[useCustomers] Error refreshing one customer:', error);
      }),
    [dispatch, user?.id, config]
  );

  const onInitCustomers = useCallback(
    () => {
      if (!customers[0]) {
        onRefreshCustomers();
      }
    },
    [customers, onRefreshCustomers],
  );

  const onResetCustomers = useCallback(
    () => {
      dispatch(resetCustomers());
    },
    [dispatch],
  );

  const onUpdateCustomers = useCallback(
    (data) => {
      dispatch(updateCustomers({ data }))
    },
    [dispatch]
  );

  const onUpdateCustomer = useCallback(
    (customer) => {
      if (customerSelected.email === customer.email) dispatch(selectCustomer(customer));
      dispatch(updateCustomer(customer))
    },
    [dispatch, customerSelected]
  );

  // Memoized return value
  return useMemo(
    () => ({
      allCustomers: customers,
      isLoading,
      error,
      onRefreshCustomers,
      onInitCustomers,
      onResetCustomers,
      onRefreshOneCustomer,
      onUpdateCustomers,
      onUpdateCustomer,
    }),
    [
      customers,
      isLoading,
      error,
      onRefreshCustomers,
      onInitCustomers,
      onResetCustomers,
      onRefreshOneCustomer,
      onUpdateCustomers,
      onUpdateCustomer,
    ]
  );
}
